import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router';
import { SiteDTO, SiteIntegrationTypeEnum, TriggerRosterBatchRerunRequest } from "@beable/roster-backend-client-v1";
import { triggerSync } from "../../services/FileUploadService";
import { Button, Grid, Link, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Replay } from "@mui/icons-material";
import { getStyles } from "./styles";
import rosterBackendService from "../../services/RosterBackendService";
import authService from "../../services/AuthService";

interface FileUploadProps {
  site: SiteDTO;
}

export default function BatchTrigger({ site }: FileUploadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const navigate = useNavigate();
  const [triggerErr, setTriggerErr] = useState<boolean>(false);
  const [triggerSuccess, setTriggerSuccess] = useState<boolean>(false);

  useEffect(() => {
    resetState();
  }, [site]);

  const resetState = () => {
    setTriggerErr(false);
    setTriggerSuccess(false);
  };

  const handleRosterRerun = async () => {
    const req = new TriggerRosterBatchRerunRequest();
    req.siteId = site.id;
    const result = await rosterBackendService.triggerRosterBatchRerun(req);

    resetState();
    if (result.isOk()) {
      setTriggerSuccess(true);
    } else {
      setTriggerErr(true);
    }
  };

  const handleRosterTrigger = async () => {
    const result = await triggerSync(site.id, site.integrationType);

    resetState();
    if (result.isOk()) {
      setTriggerSuccess(true);
    } else {
      setTriggerErr(true);
    }
  };

  const goToBatchStatus = () => {
    navigate(`/batch-status`);
  };

  const isOneRosterCSV = site.integrationType === SiteIntegrationTypeEnum.OneRosterCSV;

  return (
    <Grid size={12}>
      {isOneRosterCSV ? (
        <Typography component="p" sx={{ textAlign: 'left' }}>
          Click the button below to trigger a rerun of the latest successful 
          roster batch for this site!
        </Typography>
      ) : (
        <>
          <Typography component="p" sx={{ textAlign: 'left' }}>
            By clicking the button below you will attempt to run a batch from a
            provider.
          </Typography>
          <Typography component="p" sx={{ textAlign: 'left' }}>
            Depending on whether you are set to automatic or manual mode, you will
            either let a batch run to completion (automatic mode) or up to a stalled
            state (manual mode) requiring an approval.
          </Typography>
        </>
      )}
      {isOneRosterCSV ? (
        <Button variant="contained" color="primary" onClick={handleRosterRerun} startIcon={<Replay />}>
          Trigger roster rerun
        </Button>
      ) : (
        <Button variant="contained" color="primary" onClick={handleRosterTrigger}>
          Trigger Batch Run
        </Button>
      )}
      {triggerErr ? (
        <Typography sx={{ ...styles.errMsg, textAlign: 'left' }} component="p">
          Error: Could not trigger a batch run! Please, contact Beable support.
        </Typography>
      ) : undefined}
      {triggerSuccess ? (
        <>
          <Typography
            sx={{ ...styles.successMsg, textAlign: 'left' }}
            component="p"
          >
            Status: Batch run triggered successfully!
            <Link
              onClick={() => {
                goToBatchStatus();
              }}
              component="button"
              variant="body2"
              sx={{ marginLeft: '4px' }}
              underline="hover">
              Go to Batch Status
            </Link>
          </Typography>
        </>
      ) : undefined}
    </Grid>
  );
}
